<template>
  <a
    class="character-card xmov-interactive-card"
    :href="`/3d-avatar/${props.data?.identity}.html`"
    @click.prevent="goPerson"
    @mouseenter.self="onMouseenter"
    @mouseleave.self="onMouseleave"
  >
    <div class="flex-1 content overflow-hidden">
      <NewTag v-if="data.is_new" class="absolute top-[8px] left-[8px]" />
      <nuxt-link :to="`/3d-avatar/${data.identity}.html`">
        <Resource
          :src="data.cover_url"
          class="object-cover w-full"
          :resource-option="{
            format: 'webp',
            resize: {
              w: 500,
              m: 'lfit',
            },
          }"
        />
      </nuxt-link>
      <video
        :src="showVideo ? data.preview_videos?.[0] : ''"
        autoplay
        loop
        muted
        playsinline
        class="w-full h-full object-cover absolute top-0 left-0"
      />
      <div class="action">
        <div class="header flex-y-center justify-between relative" @click.stop>
          <div v-if="preview" class="pc-only">
            <Eye :data="data" class="absolute top-[3px] left-[3px]" />
          </div>
          <div
            v-if="false"
            class="flex gap-[12px] absolute top-[2px] right-[2px]"
          >
            <div v-if="collect" class="pc-only">
              <Collect
                class="collect"
                :model-value="data?.is_star_personal"
                :data="data"
                :params="{
                  model_id: data?.id || -1,
                  resource_type: RESOURCE_TYPE.PERSON,
                  model_type: COLLECTION_MAP.SOURCE, //后端没有类型的概念，类型松散，该字段有可能不存在
                  source: COLLECTION_SOURCE.YOUYAN,
                }"
                @update:model-value="
                  (v) => $emit('change', ['is_star_personal', v])
                "
              />
            </div>
            <TemplateShare
              v-if="share"
              :isCard="true"
              :title="$t('fen-xiang-3d-shu-zi-ren')"
              :trackParams="{
                source: resourceLocation?.source,
                id: data.id,
                tags: data.tags,
                look_name: data.look_name,
                name: data.name,
              }"
              :eventName="TRACK_EVENT.YOUYAN_PLATFORM_SHARE_CHARACTER"
              :params="{
                share_ids: [data?.id],
                model_type: COLLECTION_MAP.SOURCE,
              }"
              :shareToInnerUser="false"
              :linkUrl="`${location?.origin}/3d-avatar/${data.identity}.html`"
            ></TemplateShare>
          </div>
        </div>
        <div v-if="action" class="pc-only">
          <div class="footer">
            <CardAction
              v-if="!isChinese()"
              :leftTip="$t('bian-ji-ren-wu')"
              :rightText="$t('yong-ta-chuang-zuo')"
              @click-left="createOps('image')"
              @click-right="createOps('person')"
            >
              <template #leftIcon>
                <iconpark-icon name="edit"></iconpark-icon>
              </template>
            </CardAction>
            <template v-else>
              <a-button
                class="btns min-w-[100px] font-[500]"
                @click.stop="createOps('image')"
              >
                {{ $t('bian-ji-ren-wu') }}
              </a-button>
              <a-button
                type="primary"
                class="btns min-w-[116px] font-[500]"
                @click.stop="createOps('person')"
              >
                {{ $t('yong-ta-chuang-zuo') }}
              </a-button>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="flex-y-center justify-between mt-[12px] flex-shrink-0">
      <div class="text-14 text-font-1 truncate flex-1 font-bold">
        {{ data.name }}
      </div>
      <span
        v-if="tagArr?.length"
        class="tags"
        :style="{
          color: tagColor[tagArr[0].outer_id!]?.color,
          background: tagColor[tagArr[0].outer_id!]?.bg,
        }"
      >
        {{ tagArr[0].tag_name }}
      </span>
    </div>
  </a>
</template>

<script setup lang="ts">
import CardAction from '~/components/card-action/index.vue'
import Collect from '@/features/collect/index.vue'
import { TemplateShare } from '@/features/template/components/templateShare'
import { Eye } from '@/features/pageList'
import { ACTOR_NAV, CONFIG_NAV, CONTENT_TABS, USER_LEVEL } from '~/constants'
import { createWorkByCharacter } from '~/features/createWork/utils'
import type { Character } from '~/api/interface/character'
import { SESSION_KEY } from '~/constants/config'

defineEmits(['change'])

const props = withDefaults(
  defineProps<{
    data: Character
    collect?: boolean
    share?: boolean
    preview?: boolean
    action?: boolean
    target?: '_blank' | '_parent' | '_self' | '_top' | string
  }>(),
  {
    collect: true,
    share: true,
    action: true,
    preview: true,
  }
)

const { $t } = useNuxtApp()
const router = useRouter()
const tags = useCharacterTags()
const userInfo = useUserInfo()
const resourceLocation = injectResourceLocation()
const showVideo = ref(false)
const mouseover = ref(false)
let timer: any = null

/** 暂时放在这里，后面如果需要点击tag跳转用得着 */
const rightsTag = computed(() => {
  return tags.value.tags.find((item) => item.outer_id === 'character_rights')
})

const tagArr = computed(() => {
  if (rightsTag.value) {
    const childTagIdArr = props.data.tag_ids[rightsTag.value?.tag_id]
    if (!childTagIdArr) {
      return []
    }
    return rightsTag.value.children?.filter((child) => {
      return childTagIdArr?.includes(child.tag_id)
    })
  }

  return []
})

const tagColor: any = {
  character_person: { bg: '#B9FCFF', color: '#00C5CD' },
  character_enterprise: { bg: '#D5D2FF', color: '#766FFF' },
}

const location = computed(() => {
  if (import.meta.client) {
    return window.location
  }

  return null
})

const onMouseenter = () => {
  mouseover.value = true
  timer = setTimeout(() => {
    showVideo.value = true
  }, 200)
}

const onMouseleave = () => {
  showVideo.value = false
  mouseover.value = false
  clearTimeout(timer)
  timer = null
}

const createOps = (type: 'image' | 'person') => {
  // 前端判断当前资产用户是否可用
  if (
    userInfo.value?.rights_type &&
    props.data.config_type > userInfo.value?.rights_type
  ) {
    const text = USER_LEVEL()[props.data?.config_type]?.name

    message.warning(`当前角色是${text}资产，如需使用请升级`)
    return
  }

  if (type === 'image') {
    createWorkByCharacter(props.data.id, CONFIG_NAV.ACTOR, ACTOR_NAV.CHARACTER)
  } else {
    createWorkByCharacter(
      props.data.id,
      CONFIG_NAV.CONTENT,
      CONTENT_TABS.CHARACTER
    )
  }
}

function addTrackerPoint() {
  if (resourceLocation?.source) {
    sessionSetItem(SESSION_KEY.CHARACTER_SOURCE, resourceLocation.source)
  }
}

const goPerson = () => {
  addTrackerPoint()
  navigateTo(`/3d-avatar/${props.data?.identity}.html`, {
    open: {
      target: props?.target || '_blank',
    },
  })
}
</script>

<style lang="scss" scoped>
.character-card {
  @apply rounded-[12px] p-[12px] bg-back-1 flex flex-col relative;
  border: 1px solid var(--back-4);

  .content {
    @apply bg-back-3 rounded-[10px] relative cursor-pointer;

    .action {
      @apply absolute top-0 left-0 w-full h-full p-[6px];
      .header {
        display: none;
      }
      .footer {
        @apply absolute flex-y-center gap-[12px] justify-center left-1/2 -translate-x-1/2;
        transition: bottom 0.12s ease-out;
        bottom: -40px;
        visibility: hidden;
        .btns {
          @apply rounded-[24px] h-[32px];
        }
      }
    }
  }
  &:hover {
    .content {
      .new-tag {
        display: none;
      }
    }
    .action {
      @apply block;
      .header {
        display: flex;
      }
      .footer {
        @apply bottom-[18px];
        visibility: visible;
      }
    }
  }
  .tags {
    @apply rounded-[10px] p-[3px_8px] inline-block text-[12px];
    line-height: 1;
  }
}
</style>
